/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CustomizationData,
  AdditionalSettings,
  paymentClient,
  PaymentStatusData,
  SupportContactInformationRecord,
} from '../../../../client/base';
import { useBoolean } from '@britishcouncil/react-common';
import { checkIfImageOrDefault, goToExpiredPage, goToUrl } from '../../../../utils/utils';
import { GoogleAnalyticsHelpers } from '../../../../googleAnalytics/GoogleAnalyticsHelpers';
import { setAxiosHeaders } from '../../../../utils/auditInterceptorsAxiosConfig';
import { EolChat } from '../../EolChat';
import {
  ConfirmPaymentButton,
  HeaderBar,
  ProductsSummaryProps,
  TimerSectionProps,
} from '../../../../shared';
import { useTranslation } from 'react-i18next';
import { configureTrackingProps, EntityType, logError } from '../../../../utils/logger-utils';
import { startAppInsightTracking } from '../../applicatonInsights';
import { Footer, FooterLinkTexts, Header } from '@britishcouncil/react-extro-ui';
import i18next, {
  getTranslationSettingsForHeader,
  initializeTranslationForWrapper,
} from '../../../../utils/i18n';
import CommonLayout from '../../../../shared/layout/commonLayout/CommonLayout';
import CommonPaymentSideSection, {
  PAYMENT_SUBMITTED_KEY,
} from '../../../../shared/layout/sideSection/payment/CommonPaymentSideSection';
import { useSearchParams, Outlet, useLocation, useNavigationType } from 'react-router';
import * as S from './paymentWrappers.styles';
import { useLoadingContext } from '../../../../App';
import PaymentMethodChanger from '../../../../shared/paymentMethodChanger/PaymentMethodChanger';
import CheckoutSkeleton from '../../../../shared/CheckoutSkeleton';
import { ErrorMsg, PageTitle, SupportSection } from '../../..';
import { PageType } from '../types';
import {
  getPaymentStatus,
  getSupportContactInformation,
} from '../../../../shared/PaymentContextClient';
import { PAYMENT_IDENTIFIER_NAME } from '../../../../utils';
import {
  PaymentMethodChangeStatus,
  usePaymentMethodContext,
  PaymentWrapperContext,
  useErrorSummaryContext,
  useExperimentContext,
} from '@contexts';
import { resolveExperimentAssignments } from '../../../../experiments/resolveAssignments';
import { useAppSettings } from '../../../../utils/appSettings';
import ErrorSummaryBox from '../../../../shared/errorSummary/ErrorSummaryBox';
import { useNavigateToErrorPage, useQueryParams } from '../../../../hooks';
import { useBootPlaceholder } from '../../../../utils/initialLoader';
import { PaymentErrorPagePath } from '../../../../routes/PathConsts';
import useCurrentBreakpoint from '@britishcouncil/react-extro-ui/dist/hooks/useCurrentBreakpoint';

interface Props {
  options?: HeaderAndFooterOptions;
}

interface HeaderAndFooterOptions {
  withTimer?: boolean;
  isTimerHidden?: boolean;
  withRedirectOnFinishedPayment?: boolean;
  isErrorPage?: boolean;
  withBackButton?: boolean;
  withSideSection?: boolean;
  customPaymentIdParam?: string;
  withPaymentMethodChanger?: boolean;
  withTitle?: boolean;
  pageType?: PageType;
  alignCenter?: boolean;
  showSidePaymentButton?: boolean;
}

const PaymentWrapper = ({
  options = {
    withTimer: false,
    isTimerHidden: false,
    withRedirectOnFinishedPayment: false,
    isErrorPage: false,
    withBackButton: false,
    withSideSection: false,
    customPaymentIdParam: undefined,
    withPaymentMethodChanger: false,
    withTitle: false,
    pageType: PageType.Payment,
    alignCenter: false,
    showSidePaymentButton: true,
  },
}: Props) => {
  const { isLoading, setIsLoading } = useLoadingContext();
  const [customizationData, setCustomizationData] = useState<CustomizationData>({});
  const [isActive, setIsActive] = useBoolean(false);
  const [loadDataFailed, setLoadDataFailed] = useBoolean(false);
  const [paymentButtonVisible, setPaymentButtonVisible] = useBoolean(false);
  const [customPaymentButtonText, setCustomPaymentButtonText] = useState<string>();
  const [isCollectingBillingDetails, setIsCollectingBillingDetails] = useBoolean();
  const [isTranslationAllowed, setIsTranslationAllowed] = useBoolean();
  const [additionalSettings, setAdditionalSettings] = useState<AdditionalSettings>();
  const [getStatusResponse, setGetStatusResponse] = useState<PaymentStatusData>();
  const [timeoutDate, setTimeoutDate] = useState<Date>();
  const [isTimeoutExpired, setIsTimeoutExpired] = useState<boolean>(false);
  const [productsSummary, setProductsSummary] = useState<ProductsSummaryProps>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [supportInfo, setSupportInfo] = useState<SupportContactInformationRecord>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation(['npp']);
  const location = useLocation();
  const navigationType = useNavigationType();
  const { navigateToPaymentErrorPage } = useNavigateToErrorPage();
  const {
    status,
    paymentMethodContextInitialized,
    triggerPaymentMethodChange,
    chosenPaymentMethod,
    initializePaymentMethodContext,
  } = usePaymentMethodContext();
  const { isErrorSummaryBoxVisible } = useErrorSummaryContext();
  const { initialize: initializeExperiments } = useExperimentContext();
  const queryParams = useQueryParams();
  const appSettings = useAppSettings();
  const paymentId = options.customPaymentIdParam
    ? queryParams.getParam(options.customPaymentIdParam)
    : queryParams.getPaymentId();
  const breakpoint = useCurrentBreakpoint();
  const rewritePaymentIdQueryParam = !!options.customPaymentIdParam;
  const paymentSubmitted = sessionStorage.getItem(PAYMENT_SUBMITTED_KEY(paymentId));
  const isRetry = queryParams.getParam('retry');
  const retryMessage = useRef<string>();
  const [isPayByLink, setIsPayByLink] = useBoolean(false);

  const displayPaymentButton = useMemo(() => {
    return (
      options.showSidePaymentButton &&
      paymentButtonVisible &&
      paymentSubmitted !== 'true' &&
      !loadDataFailed
    );
  }, [options.showSidePaymentButton, paymentButtonVisible, loadDataFailed, paymentSubmitted]);

  useEffect(() => {
    // The ?retry banner is set once, from a param consumed on arrival. Boot-time URL
    // correction (context init picks the method and replaces the URL) must not wipe it —
    // but it has no business on a status, error or expired page, and every pay-now route,
    // only those, carries the payment method changer.
    const isPayNowPage = !!options.withPaymentMethodChanger;
    setErrorMessage(isPayNowPage ? retryMessage.current : undefined);

    if (!options.withTimer) {
      setTimeoutDate(undefined);
      setIsTimeoutExpired(false);
    }

    // 'POP' = browser back/forward (as opposed to PUSH/REPLACE we trigger ourselves).
    // Coming back into a payment page that has meanwhile finished must not show the form again.
    const isBackOrForwardNavigation = navigationType === 'POP';
    let cancelled = false;

    if (isBackOrForwardNavigation && isActive && options.withRedirectOnFinishedPayment) {
      getPaymentStatus(paymentId)
        .then((status) => {
          if (cancelled) return;
          if (status.data.isFinished && status.data.redirectUrl) {
            window.location.href = status.data.redirectUrl;
          }
        })
        .catch((err) => {
          if (cancelled) return;
          navigateToPaymentErrorPage(err);
        });
    }

    // One PaymentWrapper instance serves every /payment/* route, so it is not unmounted
    // between them — without this, a response arriving after the user moved on would
    // redirect or error out the page they are on now.
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  useEffect(() => {
    if (isRetry) {
      retryMessage.current = 'npp:failure.payment.retryMessage';
      setErrorMessage(retryMessage.current);
      searchParams.delete('retry');
      setSearchParams(searchParams);
    }
  }, [isRetry]);

  useEffect(() => {
    if (rewritePaymentIdQueryParam) {
      setSearchParams((searchParams) => {
        searchParams.set(PAYMENT_IDENTIFIER_NAME, paymentId);
        return searchParams;
      });
    }
  }, [paymentId, rewritePaymentIdQueryParam, setSearchParams]);

  useEffect(() => {
    if (paymentId)
      getSupportContactInformation(paymentId).then((result) => {
        if (result) {
          setSupportInfo(result);
        }
      });
  }, [paymentId]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      if (!paymentId) {
        goToUrl(PaymentErrorPagePath);
      }

      localStorage.setItem(PAYMENT_IDENTIFIER_NAME, paymentId);
      try {
        const configurationData = (await paymentClient.getPaymentConfiguration(paymentId)).data;

        configureTrackingProps(
          configurationData.paymentId,
          EntityType.payment,
          configurationData.correlationSessionId!
        );

        startAppInsightTracking();

        if (configurationData.additionalSettings) {
          setAdditionalSettings(configurationData.additionalSettings);
        }

        setAxiosHeaders(configurationData.correlationSessionId!, paymentId);
        setCustomizationData(configurationData.customizationData!);
        initializeExperiments(
          resolveExperimentAssignments(configurationData.abExperimentAssignments, {
            environment: appSettings.settings.environment,
            searchParams,
          })
        );
        setIsCollectingBillingDetails(configurationData.collectBillingDetails);
        setIsTranslationAllowed(configurationData.isTranslationAllowed);
        setIsPayByLink(configurationData.applicationCode === 'PBL');
        initializeTranslationForWrapper(paymentId, configurationData.customizationData);
        options.withTimer && setTimeoutDate(new Date(Date.now() + configurationData.timeoutValue));
      } catch (err) {
        navigateToPaymentErrorPage(err);
        setIsActive(true);
        return;
      } finally {
        setIsLoading(false);
      }

      try {
        if (!options.withRedirectOnFinishedPayment) {
          return;
        }

        const status = await getPaymentStatus(paymentId);
        if (status.data.isFinished) {
          window.location.href = status.data.redirectUrl;
          return;
        }

        if (status.data.productsSummary) {
          setProductsSummary({
            items: status.data.productsSummary.lineItems.map((x) => {
              return {
                label: x.description,
                amount: x.amountFormatted,
              };
            }),
            orderId: status.data.correlationId,
            total: status.data.productsSummary.amountFormattedWithCurrency,
          });
        }

        setGetStatusResponse(status.data);
      } catch (err) {
        navigateToPaymentErrorPage(err);
      } finally {
        setIsActive();
      }
    };

    const handleErrorPage = async () => {
      try {
        let customizationData: CustomizationData | undefined = undefined;
        if (paymentId) {
          const configurationData = (await paymentClient.getPaymentConfiguration(paymentId)).data;
          customizationData = configurationData.customizationData;
        }
        initializeTranslationForWrapper(paymentId, customizationData);
      } catch (e) {
        logError(e);
      }
      setIsActive();
    };

    //reset loader when page is loaded from BFCache
    window.onpageshow = (e) => e.persisted && setIsLoading(false);
    options.isErrorPage ? handleErrorPage() : loadData();
  }, [paymentId]);

  // Holds the boot overlay across the context-init window, which has no placeholder of its own.
  useBootPlaceholder(!!options.withRedirectOnFinishedPayment && !paymentMethodContextInitialized);

  useEffect(() => {
    const timeoutPayment = () => {
      paymentClient
        .timeoutPayment(paymentId)
        .then(() => {
          GoogleAnalyticsHelpers.UpdateDataLayer({
            category: 'Payment',
            action: 'Timeout',
          });
          goToExpiredPage();
        })
        .catch((err) => {
          navigateToPaymentErrorPage(err);
        });
    };
    isTimeoutExpired && !isLoading && timeoutPayment();
  }, [isTimeoutExpired, isLoading, paymentId]);

  // Initialize payment method context
  useEffect(() => {
    if (paymentMethodContextInitialized || !getStatusResponse || !paymentId || !location) {
      return;
    }

    initializePaymentMethodContext({ paymentId, queryParams, getStatusResponse, location });
  }, [getStatusResponse, paymentId, paymentMethodContextInitialized, location]);

  useEffect(() => {
    if (
      paymentMethodContextInitialized &&
      chosenPaymentMethod &&
      status === PaymentMethodChangeStatus.Init
    ) {
      triggerPaymentMethodChange();
    }
  }, [chosenPaymentMethod, status, paymentMethodContextInitialized]);

  const linkCustomTexts: FooterLinkTexts = {
    accessibility: t('npp:general.footer.accessibility'),
    dataProtection: t('npp:general.footer.dataProtection'),
    privacyAndCookies: t('npp:general.footer.privacyAndCookies'),
    termsOfUse: t('npp:general.footer.termsOfUse'),
  };

  const timerOptions: TimerSectionProps | undefined =
    options.withTimer && timeoutDate
      ? {
          hidden: options.isTimerHidden,
          timeoutDate,
          onComplete: () => setIsTimeoutExpired(true),
        }
      : undefined;

  const getPageTitle = () => {
    switch (options.pageType) {
      case PageType.Status:
        return t('npp:paymentStatusPage.title');

      case PageType.Payment:
      default:
        return t('npp:general.payOnline');
    }
  };

  if (!isActive) {
    return (
      <S.Root>
        <CheckoutSkeleton
          alignCenter={options.alignCenter}
          logoUrl={checkIfImageOrDefault(customizationData.paymentPageLogoImageUrl)}
        />
      </S.Root>
    );
  }

  return (
    <S.Root>
      <Header
        slogan={t('npp:general.checkout')}
        translations={getTranslationSettingsForHeader(
          paymentId,
          isTranslationAllowed,
          customizationData
        )}
        logoUrl={checkIfImageOrDefault(customizationData.paymentPageLogoImageUrl)}
      />
      <HeaderBar
        withBackButton={!isPayByLink && options.withBackButton}
        entity={{ type: 'Payment', id: paymentId }}
      />

      <CommonLayout
        key={i18next.language}
        alignCenter={options.alignCenter}
        topSection={
          <>
            {isErrorSummaryBoxVisible && <ErrorSummaryBox />}
            {errorMessage && <ErrorMsg content={errorMessage} />}
            {options.withTitle && <PageTitle title={getPageTitle()} />}
          </>
        }
        mainSection={
          <>
            {options.withPaymentMethodChanger && (
              <PaymentMethodChanger queryParams={queryParams} setErrorMessage={setErrorMessage} />
            )}
            <Outlet
              context={
                {
                  paymentId,
                  queryParams,
                  isLoading,
                  isCollectingBillingDetails,
                  setIsLoading,
                  onErrorMessageChange: setErrorMessage,
                  loadDataFailed,
                  setLoadDataFailed,
                  setCustomPaymentButtonText,
                  setPaymentButtonVisible,
                } satisfies PaymentWrapperContext
              }
            />
            {displayPaymentButton && breakpoint !== 'lg' && (
              <ConfirmPaymentButton
                formId="payment-form"
                id="btn-pay"
                customText={customPaymentButtonText}
                paymentPageLogoImageUrl={customizationData.paymentPageLogoImageUrl}
                onBeforeSubmit={() => setErrorMessage(undefined)}
              />
            )}
            {!isLoading && supportInfo && (
              <SupportSection
                supportInfo={supportInfo.supportContactInformation}
                correlationId={supportInfo.correlationId}
              />
            )}
          </>
        }
        sideSection={
          options.withSideSection ? (
            <CommonPaymentSideSection
              timer={timerOptions}
              productSummary={productsSummary}
              displayPaymentButton={displayPaymentButton}
              paymentPageLogoImageUrl={customizationData.paymentPageLogoImageUrl}
              customPaymentButtonText={customPaymentButtonText}
              paymentId={paymentId}
              onBeforePaymentSubmit={() => setErrorMessage(undefined)}
            />
          ) : null
        }
      />

      {additionalSettings?.isSalesForceChatEnabled ? (
        <EolChat
          eolChatDeploymentId={additionalSettings.salesForceChatDeploymentId!}
          eolChatOrganisationId={additionalSettings.salesForceChatOrganisationId!}
          eolApiEndpointId={additionalSettings.salesForceApiEndpointId!}
          eolChatUserId={additionalSettings.salesForceChatUserId!}
        />
      ) : null}

      <Footer
        linkUrls={{
          accessibility: customizationData.accessibilityUrl,
          dataProtection: customizationData.dataProtectionUrl,
          privacyAndCookies: customizationData.privacyAndCookiesUrl,
          termsOfUse: customizationData.termsOfUseUrl,
        }}
        copyrightOwner={customizationData.copyrightOwner}
        linkCustomTexts={linkCustomTexts}
      />
    </S.Root>
  );
};

export default PaymentWrapper;
